import axios from 'axios'
import u from '@/utils'
import moment from 'moment'

const state = {
  loadingWorkflows: false,
  workflows: [],
  workflowsWithProjection: [],
  workflowProfiles: {},
  workflowBots: {},
  workflowProcessVersion: [],
  workflowXML: {}
}

const getters = {
  getWorkflow(state) {
    return state.workflows
  },
  getWorkflowWithProjection(state) {
    return state.workflowsWithProjection
  }
}

const mutations = {
  setWorkflowsLoading(state, isLoading) {
    state.loadingWorkflows = isLoading
  },
  setWorkflows(state, workflows) {
    state.workflows = workflows
  },
  setWorkflowWithProjection(state, workflows) {
    state.workflowsWithProjection = workflows
  },
  setWorkflowProfiles(state, payload) {
    state.workflowProfiles = {
      ...state.workflowProfiles,
      [payload.workflow.hexId]: payload.profiles
    }
  },
  setWorkflowBots(state, payload) {
    state.workflowBots = {
      ...state.workflowBots,
      [payload.workflow.hexId]: payload.bots
    }
  },
  updateWorkflow(state, workflow) {
    let index = state.workflows.findIndex(item => item.hexId === workflow.hexId)
    state.workflows.splice(index, 1, workflow)
  },
  setWorkflowProcessVersion(state, versions) {
    state.workflowProcessVersion = versions
  },
  setWorkflowXML(state, diagram) {
    state.workflowXML = diagram
  }
}

const actions = {
  async fetchWorkflows({ commit }) {
    commit('setWorkflowsLoading', true)
    let response = await axios.get('/api/v1/workflows')
    let workflows = [...response.data._embedded.workflows]
    workflows = workflows.map(workflow => u.processResp(workflow))
    commit('setWorkflows', workflows)
    commit('setWorkflowsLoading', false)
    return workflows
  },

  async fetchWorkflowsWithProjection({ commit }) {
    let response = await axios.get('/api/v1/workflows?projection=withMetrics')
    let workflows = [...response.data._embedded.workflows]
    workflows = workflows.map(workflow => u.processResp(workflow))
    commit('setWorkflowWithProjection', workflows)
    return workflows
  },

  async fetchWorkflow({ commit }, hexId) {
    let response = await axios.get(`/api/v1/workflows/${hexId}`)
    let workflow = u.processResp(response.data)
    commit('updateWorkflow', workflow)
    return workflow
  },

  async fetchWorkflowSelfService({ commit }, hexId) {
    let response = await axios.get(`/api/v1/workflows/${hexId}/selfService`)
    return response
  },

  async fetchWorkflowProfiles({ commit }, workflow) {
    let response = await axios.get(
      `/api/v1/workflows/${workflow.hexId}/profiles`
    )
    let profiles = [...response.data._embedded.profiles]
    profiles = profiles.map(profile => u.processResp(profile))
    commit('setWorkflowProfiles', { workflow, profiles })
    return profiles
  },

  async fetchWorkflowBots({ commit }, workflow) {
    let response = await axios.get(`/api/v1/workflows/${workflow.hexId}/bots`)
    if (response.data._embedded) {
      let bots = [...response.data._embedded.bots]
      bots = bots.map(bot => u.processResp(bot))
      commit('setWorkflowBots', { workflow, bots })
      return bots
    }
  },

  async addProfilesToWorkflow(context, payload) {
    let data = payload.profileIds
      .map(profileId => {
        return `/api/v1/profiles/${profileId}`
      })
      .join('\n')
    let response = await axios.put(
      `/api/v1/workflows/${payload.workflow.hexId}/profiles`,
      data,
      {
        headers: {
          'Content-Type': 'text/uri-list'
        }
      }
    )
    return response.data
  },

  async updateWorkflow({ commit }, { body, hexId }) {
    let response = await axios.patch(`/api/v1/workflows/${hexId}`, body)
    response.data = u.processResp(response.data)
    commit('updateWorkflow', response.data)
    return response.data
  },

  async setWorkflowActive({ commit, dispatch }, workflow) {
    let body = {
      active: workflow.active
    }
    try {
      let response = await axios.patch(
        `/api/v1/workflows/${workflow.hexId}`,
        body
      )
      let message = ''
      if (workflow.active) {
        message = 'Workflow activation successful!'
      } else {
        message = 'Workflow de-activation successful!'
      }
      dispatch(
        'directNotification',
        {
          message: message,
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        },
        {
          rootState: true
        }
      )
      commit('updateWorkflow', response.data)
      return response.data
    } catch (e) {
      let message = ''
      if (workflow.active) {
        message = 'Workflow activation failed!'
      } else {
        message = 'Workflow de-activation failed!'
      }
      dispatch(
        'directNotification',
        {
          message: message,
          description: e.toString(),
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        },
        {
          rootState: true
        }
      )
      throw e
    }
  },

  async fetchDefaultWorkflow() {
    let response = await axios.get('/bpmn/newDiagram.bpmn', {
      responseType: 'text'
    })
    return response.data
  },

  async saveWorkflow(context, workflowBody) {
    let response = await axios.post('/api/v1/workflows', workflowBody)
    return response.data
  },

  async deleteWorkflow(context, hexId) {
    await axios.delete(`/api/v1/workflows/${hexId}`)
  },

  async deployWorkflow(context, hexId) {
    let response = await axios.post(`/api/v1/workflows/${hexId}/deploy`)
    return response.data
  },

  async retriggerWorkflow(context, hexId) {
    let response = await axios.post(`/api/v1/workflows/${hexId}/retrigger`)
    return response.data
  },

  async queueWorkflow(context, { body, hexId }) {
    let response = await axios.post(`/api/v1/workflows/${hexId}/queue`, body)
    return response.data
  },
  async fetchWorkflowProcessVersion({ commit }, key) {
    commit('setWorkflowProcessVersion', [])
    let response = await axios.get(
      `/api/core/workflow/process/version?keyLike=${key}&sortBy=version&sortOrder=desc`
    )
    let versions = [...response.data]
    commit('setWorkflowProcessVersion', versions)
    return versions
  },
  async fetchWorkflowXML({ commit }, id) {
    commit('setWorkflowXML', {})
    let response = await axios.get(`/api/core/workflow/process/${id}/xml`)
    let xml = response.data
    commit('setWorkflowXML', xml)
    return xml
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
